<template>
    <div class="order-page">
        <header class="top-nav">
            <button type="button" class="back-btn" @click="$router.back()">
                <van-icon name="arrow-left" />
            </button>
            <h1 class="nav-title">{{ getLabel('pageOrder.navTitle', 'Órdenes de Alquiler') }}</h1>
        </header>

        <main class="page-content">
            <!-- 汇总卡 -->
            <section class="summary-card">
                <div class="summary-head">
                    <span class="summary-title">
                        <van-icon name="chart-trending-o" />
                        {{ getLabel('pageOrder.summaryTitle', 'Resumen de Alquiler') }}
                    </span>
                    <span class="summary-badge">{{ orderList.length }} {{ getLabel('pageOrder.ordersUnit', 'Órdenes') }}</span>
                </div>

                <div class="summary-main">
                    <div class="sum-box">
                        <p>{{ getLabel('pageOrder.totalRental', 'Alquiler Total') }}</p>
                        <b>{{ formatCurrency(totalRental) }}</b>
                        <small class="up">↑ {{ activeCount }} {{ getLabel('pageOrder.activeUnit', 'En curso') }}</small>
                    </div>
                    <div class="sum-box green">
                        <p>{{ getLabel('pageOrder.totalIncome', 'Ingresos Totales') }}</p>
                        <b>{{ formatCurrency(totalIncome) }}</b>
                        <small>✓ {{ completedCount }} {{ getLabel('pageOrder.completedUnit', 'Completado') }}</small>
                    </div>
                </div>

                <div class="sum-mini">
                    <div class="mini-box">
                        <span>{{ getLabel('pageOrder.roi', 'Rendimiento') }}</span>
                        <b class="cyan">{{ rendimientoAvg.toFixed(2) }}%</b>
                    </div>
                    <div class="mini-box">
                        <span>{{ getLabel('pageOrder.active', 'En curso') }}</span>
                        <b class="cyan">{{ activeCount }}</b>
                    </div>
                    <div class="mini-box">
                        <span>{{ getLabel('pageOrder.completed', 'Completado') }}</span>
                        <b class="cyan">{{ completedCount }}</b>
                    </div>
                </div>
            </section>

            <!-- 筛选 Tab -->
            <nav class="filter-tabs">
                <button
                    v-for="tab in tabs"
                    :key="tab.key"
                    type="button"
                    class="filter-tab"
                    :class="{ active: activeTab === tab.key }"
                    @click="activeTab = tab.key"
                >
                    {{ tab.label }}
                </button>
            </nav>

            <!-- 订单卡片 -->
            <section v-if="filteredList.length" class="order-list">
                <article
                    v-for="item in filteredList"
                    :key="item.id || item.oid || item.time"
                    class="order-card"
                >
                    <div class="card-banner">
                        <img :src="getCover(item)" alt="" class="banner-img">
                        <div class="banner-overlay"></div>
                        <div class="banner-top">
                            <h3>{{ getOrderTitle(item) }}</h3>
                            <span class="banner-date">{{ item.time || "--" }}</span>
                        </div>
                        <div class="banner-bottom">
                            <span class="order-sn">{{ getOrderId(item) }}</span>
                        </div>
                        <span class="status-tag" :class="statusClass(item)">{{ statusText(item) }}</span>
                    </div>

                    <div class="stat-grid">
                        <div class="stat">
                            <p>{{ getLabel('pageOrder.rentAmount', 'Monto de Alquiler') }}</p>
                            <b>{{ formatCurrency(item.money) }}</b>
                        </div>
                        <div class="stat">
                            <p>{{ getLabel('pageOrder.currentIncome', 'Ingresos Actuales') }}</p>
                            <b class="green">{{ formatCurrency(currentIncome(item)) }}</b>
                        </div>
                    </div>

                    <div class="stat-grid">
                        <div class="stat">
                            <p>{{ getLabel('pageOrder.expectedIncome', 'Ingresos Esperados') }}</p>
                            <b>{{ formatCurrency(expectedTotal(item)) }}</b>
                        </div>
                        <div class="stat text-right">
                            <p>{{ getLabel('pageOrder.roiShort', 'Rendimiento') }}</p>
                            <b class="gold">+{{ rendimiento(item).toFixed(1) }}%</b>
                        </div>
                    </div>

                    <div class="progress-block">
                        <div class="progress-head">
                            <span>{{ getLabel('pageOrder.rentalProgress', 'Progreso de Alquiler') }}</span>
                            <span class="progress-days">{{ elapsedDays(item) }}/{{ item.day || 0 }} {{ getLabel('pageOrder.daysUnit', 'días') }}</span>
                        </div>
                        <div class="progress-bar">
                            <i :style="{ width: progressPercent(item) + '%' }"></i>
                        </div>
                    </div>

                    <button type="button" class="detail-btn" @click="openDetail(item)">
                        {{ getLabel('pageOrder.viewDetails', 'Comprueba los detalles') }}
                    </button>
                </article>
            </section>

            <div v-else class="empty-state">
                <van-icon name="notes-o" />
                <p>{{ getLabel('pageOrder.empty', '暂无订单记录') }}</p>
            </div>
        </main>

        <!-- 订单详情弹窗 -->
        <van-popup
            v-model="showDetail"
            position="bottom"
            round
            closeable
            class="detail-popup"
            :style="{ maxHeight: '85%' }"
        >
            <div v-if="currentOrder" class="detail-panel">
                <div class="detail-cover">
                    <img :src="getCover(currentOrder)" alt="" class="detail-cover-img">
                    <div class="detail-cover-mask"></div>
                    <div class="detail-cover-info">
                        <h3>{{ getOrderTitle(currentOrder) }}</h3>
                        <span class="status-tag" :class="statusClass(currentOrder)">{{ statusText(currentOrder) }}</span>
                    </div>
                </div>

                <div class="detail-body">
                    <div class="detail-row">
                        <span class="label">{{ getLabel('pageOrder.orderNo', 'Nº de pedido') }}</span>
                        <span class="value">{{ getOrderId(currentOrder) }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="label">{{ getLabel('pageOrder.startTime', 'Fecha de inicio') }}</span>
                        <span class="value">{{ currentOrder.time || '--' }}</span>
                    </div>
                    <div class="detail-row" v-if="currentOrder.status === 1 && currentOrder.time2 && currentOrder.time2.indexOf('0000') !== 0">
                        <span class="label">{{ getLabel('pageOrder.endTime', 'Fecha de finalización') }}</span>
                        <span class="value">{{ currentOrder.time2 }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="label">{{ getLabel('pageOrder.period', 'Plazo de alquiler') }}</span>
                        <span class="value">{{ currentOrder.day || 0 }} {{ getLabel('pageOrder.daysUnit', 'días') }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="label">{{ getLabel('pageOrder.elapsed', 'Días transcurridos') }}</span>
                        <span class="value">{{ elapsedDays(currentOrder) }} / {{ currentOrder.day || 0 }}</span>
                    </div>

                    <div class="detail-divider"></div>

                    <div class="detail-row">
                        <span class="label">{{ getLabel('pageOrder.rentAmount', 'Monto de Alquiler') }}</span>
                        <span class="value strong">{{ formatCurrency(currentOrder.money) }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="label">{{ getLabel('pageOrder.currentIncome', 'Ingresos Actuales') }}</span>
                        <span class="value strong green">{{ formatCurrency(currentIncome(currentOrder)) }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="label">{{ getLabel('pageOrder.expectedIncome', 'Ingresos Esperados') }}</span>
                        <span class="value strong">{{ formatCurrency(expectedTotal(currentOrder)) }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="label">{{ getLabel('pageOrder.dailyIncome', 'Ingreso diario aprox.') }}</span>
                        <span class="value cyan">{{ formatCurrency(dailyIncome(currentOrder)) }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="label">{{ getLabel('pageOrder.roiShort', 'Rendimiento') }}</span>
                        <span class="value gold">+{{ rendimiento(currentOrder).toFixed(2) }}%</span>
                    </div>

                    <div class="progress-block">
                        <div class="progress-head">
                            <span>{{ getLabel('pageOrder.rentalProgress', 'Progreso de Alquiler') }}</span>
                            <span class="progress-days">{{ progressPercent(currentOrder) }}%</span>
                        </div>
                        <div class="progress-bar">
                            <i :style="{ width: progressPercent(currentOrder) + '%' }"></i>
                        </div>
                    </div>
                </div>
            </div>
        </van-popup>
    </div>
</template>

<script>
import Vue from "vue";
import Fetch from "../../utils/fetch";
import { Icon, Popup } from "vant";

Vue.use(Icon);
Vue.use(Popup);

export default {
    name: "order",
    data() {
        return {
            lang: this.$i18n.locale || "zh_cn",
            activeTab: "all",
            showDetail: false,
            currentOrder: null,
            data: {
                list: [],
                on_money: 0,
                on_apr_money: 0,
                ok_apr_money: 0,
            },
            appOriginalStyles: null,
            bodyOriginalBackground: "",
        };
    },
    computed: {
        tabs() {
            return [
                { key: "all", label: this.getLabel('pageOrder.tabAll', 'Todos') },
                { key: "active", label: this.getLabel('pageOrder.tabActive', 'En curso') },
                { key: "completed", label: this.getLabel('pageOrder.tabCompleted', 'Completado') },
            ];
        },
        orderList() {
            return Array.isArray(this.data.list) ? this.data.list : [];
        },
        filteredList() {
            if (this.activeTab === "active") {
                return this.orderList.filter((item) => Number(item.status) === 0);
            }
            if (this.activeTab === "completed") {
                return this.orderList.filter((item) => Number(item.status) === 1);
            }
            return this.orderList;
        },
        totalRental() {
            return Number(this.data.on_money || 0);
        },
        totalIncome() {
            return Number(this.data.on_apr_money || 0) + Number(this.data.ok_apr_money || 0);
        },
        activeCount() {
            return this.orderList.filter((i) => Number(i.status) === 0).length;
        },
        completedCount() {
            return this.orderList.filter((i) => Number(i.status) === 1).length;
        },
        rendimientoAvg() {
            const base = Number(this.data.on_money || 0);
            const income = Number(this.data.on_apr_money || 0) + Number(this.data.ok_apr_money || 0);
            return base > 0 ? income / base * 100 : 0;
        },
    },
    created() {
        this.$parent.footer("order", false);
    },
    mounted() {
        this.applyFullScreenLayout();
        this.start();
    },
    beforeDestroy() {
        this.restoreFullScreenLayout();
    },
    methods: {
        getLabel(key, defaultText) {
            return this.$te(key) ? this.$t(key) : defaultText;
        },
        applyFullScreenLayout() {
            const app = document.getElementById("app");
            if (!app) {
                return;
            }
            this.appOriginalStyles = {
                maxWidth: app.style.maxWidth,
                width: app.style.width,
                margin: app.style.margin,
                backgroundColor: app.style.backgroundColor,
                paddingBottom: app.style.paddingBottom,
            };
            this.bodyOriginalBackground = document.body.style.background;
            app.style.maxWidth = "100%";
            app.style.width = "100%";
            app.style.margin = "0";
            app.style.backgroundColor = "#05070f";
            app.style.paddingBottom = "0";
            document.body.style.background = "#05070f";
        },
        restoreFullScreenLayout() {
            const app = document.getElementById("app");
            if (app && this.appOriginalStyles) {
                app.style.maxWidth = this.appOriginalStyles.maxWidth || "";
                app.style.width = this.appOriginalStyles.width || "";
                app.style.margin = this.appOriginalStyles.margin || "";
                app.style.backgroundColor = this.appOriginalStyles.backgroundColor || "";
                app.style.paddingBottom = this.appOriginalStyles.paddingBottom || "";
            }
            document.body.style.background = this.bodyOriginalBackground || "";
        },
        async start() {
            const res = await Fetch("/user/order");
            this.data = res.data || { list: [], on_money: 0, on_apr_money: 0, ok_apr_money: 0 };
        },
        getOrderTitle(item) {
            return item.spanish || item[this.lang] || item.title || item.name || this.getLabel("pageOrder.navTitle", "Orden");
        },
        getOrderId(item) {
            return item.order_sn || item.sn || item.no || item.oid || ("#" + (item.id || "--"));
        },
        getCover(item) {
            const raw = item.cover || item.img || item.pic || item.item_img || "";
            if (!raw) return require("../images/new/niu.png");
            if (raw.startsWith("//")) return "https:" + raw;
            return raw;
        },
        statusText(item) {
            return Number(item.status) === 1
                ? this.getLabel('pageOrder.completed', 'Completado')
                : this.getLabel('pageOrder.active', 'En curso');
        },
        statusClass(item) {
            return Number(item.status) === 1 ? "completed" : "active";
        },
        currentIncome(item) {
            const money = Number(item.money || 0);
            const rate = Number(item.rate || 0);
            if (Number(item.status) === 1) {
                return money * rate * Number(item.day || 0) / 100;
            }
            const days = this.elapsedDays(item);
            return money * rate * days / 100;
        },
        expectedTotal(item) {
            const money = Number(item.money || 0);
            const rate = Number(item.rate || 0);
            return money * rate * Number(item.day || 0) / 100;
        },
        rendimiento(item) {
            const money = Number(item.money || 0);
            return money > 0 ? this.currentIncome(item) / money * 100 : 0;
        },
        elapsedDays(item) {
            const total = Number(item.day || 0);
            if (Number(item.status) === 1) {
                return total;
            }
            const start = this.parseTime(item.time);
            if (!start || !total) {
                return 0;
            }
            const elapsed = (Date.now() - start.getTime()) / (24 * 60 * 60 * 1000);
            return Math.max(0, Math.min(total, Math.floor(elapsed)));
        },
        progressPercent(item) {
            if (item.percent !== undefined && item.percent !== null && item.percent !== "") {
                const explicit = Number(item.percent || 0);
                return Math.max(0, Math.min(100, Math.round(explicit)));
            }
            if (Number(item.status) === 1) {
                return 100;
            }
            const totalDays = Number(item.day || 0);
            const start = this.parseTime(item.time);
            if (!totalDays || !start) {
                return 0;
            }
            const totalMs = totalDays * 24 * 60 * 60 * 1000;
            const elapsed = Date.now() - start.getTime();
            const percent = totalMs > 0 ? elapsed / totalMs * 100 : 0;
            return Math.max(0, Math.min(100, Math.round(percent)));
        },
        parseTime(value) {
            if (!value) {
                return null;
            }
            const normalized = String(value).replace(/-/g, "/");
            const date = new Date(normalized);
            return Number.isNaN(date.getTime()) ? null : date;
        },
        formatCurrency(value) {
            return "MXN" + Number(value || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        openDetail(item) {
            this.currentOrder = item;
            this.showDetail = true;
            // 若有额外详情接口，可在此拉取并合并到 currentOrder
            const id = item.id || item.oid || item.order_id;
            if (id) {
                Fetch("/user/order_detail", { id }).then((r) => {
                    const detail = (r && r.data) || {};
                    this.currentOrder = Object.assign({}, item, detail);
                }).catch(() => {});
            }
        },
        dailyIncome(item) {
            const money = Number(item.money || 0);
            const rate = Number(item.rate || 0);
            return money * rate / 100;
        },
    },
};
</script>

<style lang="less" scoped>
.order-page {
    min-height: 100vh;
    background: #05070f;
    color: #fff;
}

.top-nav {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    padding: 0 16px;
    background: rgba(5, 7, 15, 0.9);
    backdrop-filter: blur(12px);
}

.back-btn {
    position: absolute;
    left: 16px;
    top: 50%;
    width: 30px;
    height: 30px;
    margin-top: -15px;
    padding: 0;
    border: 1px solid rgba(90, 130, 200, 0.3);
    border-radius: 50%;
    background: rgba(20, 35, 65, 0.6);
    color: #cdd8ee;
    display: flex;
    align-items: center;
    justify-content: center;
}

.back-btn .van-icon { font-size: 18px; }

.nav-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #eef3ff;
}

.page-content {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 16px 14px 40px;
    box-sizing: border-box;
}

/* ---------- 汇总卡 ---------- */
.summary-card {
    margin-bottom: 18px;
    padding: 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 14px;
    background: linear-gradient(160deg, rgba(13, 30, 56, 0.9), rgba(8, 16, 32, 0.9));
}

.summary-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
}

.summary-title {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #e6edfb;
    font-size: 14px;
    font-weight: 700;
}

.summary-title .van-icon { color: #2fd0e6; font-size: 16px; }

.summary-badge {
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(45, 208, 230, 0.15);
    color: #2fd0e6;
    font-size: 11px;
    font-weight: 700;
}

.summary-main {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 10px;
}

.sum-box {
    padding: 12px;
    border-radius: 10px;
    background: rgba(10, 22, 44, 0.85);
}

.sum-box p {
    margin: 0 0 6px;
    color: #8a9bb8;
    font-size: 11px;
}

.sum-box b {
    display: block;
    margin-bottom: 6px;
    color: #fff;
    font-size: 17px;
    font-weight: 800;
}

.sum-box small {
    color: #2fd0e6;
    font-size: 10px;
}

.sum-box.green {
    background: rgba(20, 50, 40, 0.6);
}

.sum-box.green b { color: #3ddc84; }
.sum-box.green small { color: #3ddc84; }

.sum-mini {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.mini-box {
    padding: 10px 8px;
    border-radius: 10px;
    background: rgba(10, 22, 44, 0.6);
    text-align: center;
}

.mini-box span {
    display: block;
    margin-bottom: 4px;
    color: #8a9bb8;
    font-size: 10px;
}

.mini-box b {
    color: #2fd0e6;
    font-size: 15px;
    font-weight: 800;
}

/* ---------- 筛选 Tab ---------- */
.filter-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 18px;
}

.filter-tab {
    flex: 1;
    height: 38px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 10px;
    background: rgba(12, 24, 46, 0.6);
    color: #8a9bb8;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.filter-tab.active {
    background: rgba(45, 208, 230, 0.18);
    border-color: #2fd0e6;
    color: #2fd0e6;
}

/* ---------- 订单卡片 ---------- */
.order-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.order-card {
    padding: 0 0 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 14px;
    background: rgba(10, 20, 40, 0.7);
    overflow: hidden;
}

.card-banner {
    position: relative;
    height: 150px;
    margin-bottom: 14px;
    overflow: hidden;
    background: #0a1428;
}

.banner-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.banner-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(5, 10, 22, 0.2) 0%, rgba(5, 10, 22, 0.85) 100%);
}

.banner-top {
    position: absolute;
    top: 12px;
    left: 14px;
    right: 14px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.banner-top h3 {
    margin: 0;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    max-width: 65%;
}

.banner-date {
    color: #c5cfe4;
    font-size: 11px;
    white-space: nowrap;
}

.banner-bottom {
    position: absolute;
    left: 14px;
    right: 14px;
    bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.order-sn {
    color: #9fb0cc;
    font-size: 10px;
    letter-spacing: 0.5px;
}

.status-tag {
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
}

.status-tag.active {
    background: rgba(45, 208, 230, 0.15);
    color: #2fd0e6;
}

.status-tag.completed {
    background: rgba(61, 220, 132, 0.18);
    color: #3ddc84;
}

.card-avatar {
    position: absolute;
    right: 12px;
    top: 40px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.25);
    background: radial-gradient(circle at 35% 30%, #ffd0a0, #c45a8a 50%, #4a1a4a);
}

.card-wheel {
    position: absolute;
    right: 50px;
    top: 44px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: conic-gradient(
        #ffd25e 0 30deg, #ff6b6b 30deg 60deg,
        #4ecdc4 60deg 90deg, #6c5ce7 90deg 120deg,
        #ffd25e 120deg 150deg, #ff6b6b 150deg 180deg,
        #4ecdc4 180deg 210deg, #6c5ce7 210deg 240deg,
        #ffd25e 240deg 270deg, #ff6b6b 270deg 300deg,
        #4ecdc4 300deg 330deg, #6c5ce7 330deg 360deg
    );
    box-shadow: 0 0 8px rgba(108, 92, 231, 0.5);
}

/* ---------- 卡片金额 ---------- */
.stat-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 14px;
    padding: 0 14px;
    margin-bottom: 10px;
}

.stat p {
    margin: 0 0 4px;
    color: #8a9bb8;
    font-size: 11px;
}

.stat b {
    color: #fff;
    font-size: 15px;
    font-weight: 700;
}

.stat b.green { color: #3ddc84; }
.stat b.gold { color: #f0b53a; }

.text-right { text-align: right; }

/* ---------- 进度 ---------- */
.progress-block {
    margin: 4px 14px 14px;
}

.progress-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.progress-head span {
    color: #9fb0cc;
    font-size: 12px;
}

.progress-days {
    color: #2fd0e6 !important;
    font-weight: 700;
}

.progress-bar {
    height: 6px;
    overflow: hidden;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
}

.progress-bar i {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #2fd0e6, #27b6d6);
    box-shadow: 0 0 8px rgba(45, 208, 230, 0.5);
}

/* ---------- 详情按钮 ---------- */
.detail-btn {
    display: block;
    width: calc(100% - 28px);
    margin: 0 14px;
    height: 42px;
    border: 0;
    border-radius: 10px;
    background: linear-gradient(90deg, #2fd0e6, #27b6d6);
    color: #04222b;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
}

/* ---------- 空状态 ---------- */
.empty-state {
    padding: 90px 20px;
    text-align: center;
    color: rgba(255, 255, 255, 0.34);
}

.empty-state .van-icon {
    font-size: 48px;
    color: rgba(45, 208, 230, 0.35);
}

.empty-state p {
    margin: 14px 0 0;
    font-size: 14px;
}

/* ---------- 详情弹窗 ---------- */
.detail-popup {
    background: #0a1428;
    color: #fff;
    overflow: hidden;
}

.detail-panel {
    padding-bottom: 20px;
}

.detail-cover {
    position: relative;
    height: 160px;
    overflow: hidden;
}

.detail-cover-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.detail-cover-mask {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(5,10,22,0.1) 0%, rgba(5,10,22,0.9) 100%);
}

.detail-cover-info {
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 14px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 10px;
}

.detail-cover-info h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 800;
    color: #fff;
    max-width: 70%;
    line-height: 1.3;
}

.detail-body {
    padding: 16px;
}

.detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    font-size: 13px;
}

.detail-row .label {
    color: #8a9bb8;
}

.detail-row .value {
    color: #e6edfb;
    font-weight: 600;
}

.detail-row .value.strong {
    font-size: 15px;
    font-weight: 800;
    color: #fff;
}

.detail-row .value.green { color: #3ddc84; }
.detail-row .value.cyan { color: #2fd0e6; }
.detail-row .value.gold { color: #f0b53a; }

.detail-divider {
    height: 1px;
    margin: 6px 0;
    background: rgba(70, 110, 190, 0.2);
}

.detail-body .progress-block {
    margin: 14px 0 0;
}
</style>
